import { useState, useCallback, useEffect } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { ZoomIn, Circle } from "lucide-react";

const ASPECTS = [
  { key: "wide", label: "Wide", value: 3 / 1 },
  { key: "standard", label: "Standard", value: 2 / 1 },
  { key: "square", label: "Square", value: 1 },
];

function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (e) => reject(e));
    img.src = url;
  });
}

async function getCroppedBlob(imageSrc, cropPixels, roundPct) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const w = Math.max(1, Math.round(cropPixels.width));
  const h = Math.max(1, Math.round(cropPixels.height));
  canvas.width = w;
  canvas.height = h;
  const r = Math.round((Math.min(w, h) / 2) * (roundPct / 100));
  if (r > 0) {
    ctx.beginPath();
    ctx.moveTo(r, 0);
    ctx.lineTo(w - r, 0);
    ctx.arcTo(w, 0, w, r, r);
    ctx.lineTo(w, h - r);
    ctx.arcTo(w, h, w - r, h, r);
    ctx.lineTo(r, h);
    ctx.arcTo(0, h, 0, h - r, r);
    ctx.lineTo(0, r);
    ctx.arcTo(0, 0, r, 0, r);
    ctx.closePath();
    ctx.clip();
  }
  ctx.drawImage(image, cropPixels.x, cropPixels.y, w, h, 0, 0, w, h);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png"));
}

export function LogoCropper({ file, onCancel, onCropped }) {
  const [imageSrc, setImageSrc] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspectKey, setAspectKey] = useState("wide");
  const [roundPct, setRoundPct] = useState(0);
  const [cropPixels, setCropPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setImageSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onCropComplete = useCallback((_area, pixels) => setCropPixels(pixels), []);
  const aspect = ASPECTS.find((a) => a.key === aspectKey)?.value || 3;
  const roundShape = aspectKey === "square" && roundPct >= 100;

  const apply = async () => {
    if (!cropPixels) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(imageSrc, cropPixels, roundPct);
      onCropped(blob);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!file} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-lg" data-testid="logo-cropper">
        <DialogHeader>
          <DialogTitle className="font-heading">Frame your logo</DialogTitle>
        </DialogHeader>
        <div className="relative w-full h-64 rounded-xl overflow-hidden bg-[repeating-conic-gradient(#e7e2d8_0_25%,#f4f1ea_0_50%)] bg-[length:24px_24px]">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              cropShape={roundShape ? "round" : "rect"}
              showGrid={false}
              restrictPosition={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>
        <div className="space-y-3 pt-1">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-ink/60 w-16">Shape</span>
            <div className="flex items-center gap-1 rounded-full bg-sand-100 p-0.5">
              {ASPECTS.map((a) => (
                <button key={a.key} type="button" data-testid={`crop-aspect-${a.key}`} onClick={() => setAspectKey(a.key)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${aspectKey === a.key ? "bg-ink text-white" : "text-ink/60 hover:text-ink"}`}>{a.label}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-ink/60 w-16 flex items-center gap-1"><ZoomIn className="h-3.5 w-3.5" /> Zoom</span>
            <input type="range" data-testid="crop-zoom" min={1} max={4} step={0.01} value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))} className="flex-1 accent-ink" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-ink/60 w-16 flex items-center gap-1"><Circle className="h-3.5 w-3.5" /> Round</span>
            <input type="range" data-testid="crop-round" min={0} max={100} step={1} value={roundPct}
              onChange={(e) => setRoundPct(Number(e.target.value))} className="flex-1 accent-ink" />
            <span className="text-[11px] text-ink/40 w-10 text-right">{roundPct}%</span>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" data-testid="crop-cancel" onClick={onCancel}
            className="rounded-full px-4 py-2 text-sm font-semibold text-ink/60 hover:bg-sand-100 transition-colors">Cancel</button>
          <button type="button" data-testid="crop-apply" onClick={apply} disabled={busy || !cropPixels}
            className="rounded-full bg-redline-500 text-white px-5 py-2 text-sm font-semibold hover:bg-redline-600 transition-colors disabled:opacity-50">{busy ? "Saving…" : "Use this logo"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
